import AvatarEditor from 'react-avatar-editor';
import { BaseSyntheticEvent, useRef, useState } from 'react';
import { Cell } from '@/shared/ui/Cell';
import { TNullable } from '@/shared/types';
import { Menu, MenuItem } from '@mui/material';
import { EMPTY_PHOTO_BASE64 } from '@/shared/const';
import { Typography } from '@/shared/ui/Typography';
import { useUserPhoto, useUserStore } from '@/entities/User';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import { TempImage } from './TempImage';
import { validateFile } from '../lib/validateFile';
import { useUpdatePhoto } from '../lib/useUpdatePhoto';
import { useDeletePhoto } from '../lib/useDeletePhoto';
import { formatPhoneNumber } from '../lib/formatPhoneNumber';
import UploadPhoto from '@/shared/assets/icons/uploadPhoto.svg?react';
import cls from './PhotoEdit.module.scss';

type Props = {
    setIsEditingPhoto: (condition: boolean) => void;
};

export const PhotoEdit = ({ setIsEditingPhoto }: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [anchorEl, setAnchorEl] = useState<TNullable<HTMLElement>>(null);
    const editorRef = useRef<AvatarEditor>(null);
    const [tempImage, setTempImage] = useState<TNullable<File | string>>(null);
    const [error, setError] = useState<TNullable<string>>(null);

    const {
        user: { userId },
        userData: { secondName, firstName, middleName, phoneNumber, email },
    } = useUserStore();

    const { data: photo, isError } = useUserPhoto();
    const { mutate: updatePhoto } = useUpdatePhoto({ onSuccess: () => setIsEditingPhoto(false) });
    const { mutate: deletePhoto } = useDeletePhoto({ onSuccess: () => setIsEditingPhoto(false) });

    const width = useResize();
    const isMobile = width <= 768;

    const handleMenuClick = (event: { currentTarget: HTMLElement }) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleFileChange = (e: BaseSyntheticEvent) => {
        setError(null);

        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];

            if (!validateFile(file, setError)) {
                return;
            }

            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
                const result = (event.target?.result || null) as TNullable<File | string>;

                setTempImage(result);
            };

            reader.readAsDataURL(file);
        }

        handleMenuClose();
    };

    const handleTakePhoto = () => {
        cameraInputRef.current!.click();
        handleMenuClose();
    };

    const handleRemovePhoto = () => {
        setTempImage(null);
        handleMenuClose();
    };

    const handleSave = () => {
        if (editorRef.current) {
            const canvas = editorRef.current.getImageScaledToCanvas();

            canvas.toBlob(
                (blob: TNullable<Blob>) => {
                    if (!blob) {
                        return;
                    }

                    const formData = new FormData();

                    formData.append('file', blob, 'image.png');

                    updatePhoto({ userId, formData });
                },
                'image/png',
                1
            );
        }
    };

    const handleDeletePhoto = () => {
        deletePhoto({ userId });
    };

    const open = Boolean(anchorEl);

    const userInfo = [
        { title: 'ФИО', value: `${secondName} ${firstName} ${middleName}` },
        { title: 'Номер телефона', value: formatPhoneNumber(phoneNumber) },
        { title: 'Электронная почта', value: email },
    ];

    const hasPhoto = photo && photo !== EMPTY_PHOTO_BASE64 && !isError;

    if (tempImage) {
        return <TempImage ref={editorRef} tempImage={tempImage} handleSave={handleSave} handleRemovePhoto={handleRemovePhoto} />;
    }

    return (
        <div className={cls.container}>
            <p className={cls.title}>Данные профиля</p>

            <input type="file" ref={fileInputRef} accept="image/png, image/jpg, image/jpeg" onChange={handleFileChange} />
            <input
                type="file"
                ref={cameraInputRef}
                capture="environment"
                accept="image/png, image/jpg, image/jpeg"
                onChange={handleFileChange}
            />

            <Cell error={error}>
                <button className={cls.button} type="button" onClick={handleMenuClick}>
                    {hasPhoto ? (
                        <img className={cls.image} src={photo} alt="user_profile" />
                    ) : (
                        <UploadPhoto width={185} height={185} />
                    )}
                </button>
            </Cell>

            <Menu open={open} anchorEl={anchorEl} disableScrollLock onClose={handleMenuClose}>
                <MenuItem className={cls.listItem} onClick={() => fileInputRef.current!.click()}>
                    <span>{photo ? 'Изменить фото' : 'Добавить фото'}</span>
                </MenuItem>
                {isMobile && (
                    <MenuItem className={cls.listItem} onClick={handleTakePhoto}>
                        <span>Сделать фото</span>
                    </MenuItem>
                )}
                {photo && (
                    <MenuItem className={cls.listItem} onClick={handleDeletePhoto}>
                        <span className={cls.danger}>Удалить фото</span>
                    </MenuItem>
                )}
            </Menu>

            <div className={cls.info}>
                {userInfo.map(({ title, value }) => {
                    return (
                        <div className={cls.documentsItem} key={title}>
                            <Typography as="p" variant="text_12_gray">
                                {title}
                            </Typography>
                            <p className={cls.value}>{value}</p>
                        </div>
                    );
                })}
            </div>

            <Typography as="p" variant="text_14_gray">
                Если вы хотите изменить ваши данные, обратитесь в{' '}
                <Typography as="a" variant="link" target="_blank" href="https://sberbankins.ru/feedback/">
                    поддержку
                </Typography>
            </Typography>
        </div>
    );
};
